import { PRODUCTS, strategyTypes } from 'constants/constants';
import React, { useCallback, useEffect, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Title } from 'ui-library';
import { getClassNames } from 'utils';

// --- Interfaces ---

interface Strategy {
    Id: number;
    Name: string;
    Type: string;
    Products: number[];
}

interface Product {
    Id: number;
    RiskScore: number;
}

interface CachedStrategy {
    strategyId?: number;
}

interface Props {
    strategies?: Strategy[];
    selectedStrategyId?: number;
    setSelectedStrategyId?: (id: number | undefined) => void;
    recommendedStrategy?: Partial<Strategy>;
    products?: Product[];
    cachedSelectStrategy?: CachedStrategy;
    selectedProductKey?: string;
    disabled?: boolean;
}

// --- Component ---

const StrategyList: FC<Props> = ({
    strategies = [],
    selectedStrategyId = undefined,
    setSelectedStrategyId = () => {},
    recommendedStrategy = {},
    products = [],
    cachedSelectStrategy,
    selectedProductKey,
    disabled,
}) => {
    const { t } = useTranslation();

    useEffect(() => {
        const strategyId = cachedSelectStrategy?.strategyId || recommendedStrategy?.Id;

        setSelectedStrategyId(strategyId);
    }, [recommendedStrategy?.Id, cachedSelectStrategy?.strategyId, setSelectedStrategyId]);

    const onStrategyClick = useCallback(
        (strategyId: number) => () => {
            const recStrategy = strategies.find(
                (item) => item.Id === recommendedStrategy?.Id,
            );

            // Helper to get product with highest risk score for a specific strategy
            const getTopRiskProduct = (strategyObj?: Strategy) => {
                if (!strategyObj) return undefined;

                return [...products]
                    .sort((a, b) => b.RiskScore - a.RiskScore)
                    .find((item) => strategyObj.Products.includes(item.Id));
            };

            const recommendedProduct = getTopRiskProduct(recStrategy);

            const selectedStrategy = strategies.find(
                (item) => item.Id === strategyId,
            );

            const selectedProductByScore = getTopRiskProduct(selectedStrategy);

            if (selectedStrategy?.Type === strategyTypes.INDIVIDUAL) {
                setSelectedStrategyId(strategyId);
            } else if (selectedProductByScore) {
                const isNewlySelectedRiskHigher = selectedProductByScore.RiskScore > (recommendedProduct?.RiskScore || 0);
                const isNewlySelectedRiskLower = selectedProductByScore.RiskScore < (recommendedProduct?.RiskScore || 0);

                if (isNewlySelectedRiskHigher) {
                    if (selectedProductKey?.toLowerCase() === PRODUCTS.fzp) {
                        Modal.confirm({
                            title: t('portfolio.disclaimer'),
                            content: t('portfolio.createNewPortfolio.strategy.fzpHigherRiskNotAllowed'),
                            cancelText: false,
                        });
                    } else {
                        Modal.confirm({
                            title: t('portfolio.disclaimer'),
                            content: t('portfolio.createNewPortfolio.strategy.disclaimerText.high'),
                            okText: t('clientDashboard.confirm'),
                            onOk: async () => {
                                setSelectedStrategyId(strategyId);
                            },
                            cancelText: false,
                        });
                    }

                    return;
                }

                if (isNewlySelectedRiskLower) {
                    Modal.confirm({
                        title: t('portfolio.disclaimer'),
                        content: t('portfolio.createNewPortfolio.strategy.disclaimerText.low'),
                        okText: t('clientDashboard.confirm'),
                        onOk: async () => {
                            setSelectedStrategyId(strategyId);
                        },
                        cancelText: false,
                    });

                    return;
                }

                setSelectedStrategyId(strategyId);
            }
        },
        [
            strategies,
            recommendedStrategy?.Id,
            selectedProductKey,
            products,
            setSelectedStrategyId,
            t,
        ],
    );

    return (
        <div className="fi-strategy-selection buttonRadioElementWrapper">
            <Title type={2}>{t('portfolio.createNewPortfolio.strategy')}</Title>
            <div className="new-portfolio-content form-group-controls">
                {strategies.map((strategyItem) => (
                    <Button
                        disabled={disabled}
                        key={strategyItem.Id}
                        type={selectedStrategyId === strategyItem.Id ? 'primary' : 'secondary'}
                        size="small"
                        onClick={onStrategyClick(strategyItem.Id)}
                        className={getClassNames(
                            'buttonRadioElement',
                            selectedStrategyId === strategyItem.Id
                                ? 'buttonRadioElementNotSelected'
                                : '',
                            recommendedStrategy.Id === strategyItem.Id
                                ? 'buttonRadioRecommended'
                                : '',
                        )}
                    >
                        {strategyItem?.Name}
                        {' '}
                        {recommendedStrategy.Id === strategyItem.Id && (
                            <span>
                                (
                                {t('portfolio.createNewPortfolio.strategy.recommended')}
                                )
                            </span>
                        )}
                    </Button>
                ))}
            </div>
        </div>
    );
};

export default StrategyList;
